import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form, message } from "antd";
import { useLocation } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { identitySubscriptionService } from "services/subscriptions";
import SubscriptionManagementTabs from "./SubscriptionManagementTabs";
import SubscriptionPlanDrawer from "./SubscriptionPlanDrawer";
import SubscriptionDrawer from "./SubscriptionDrawer";
import SubscriptionRawDrawer from "./SubscriptionRawDrawer";
import {
  getPlanFormValues,
  getSubscriptionFormValues,
  isFreePlanRecord,
  pageFromResponse,
  toIsoValue,
} from "./subscriptionConstants";
import {
  confirmCancelSubscription,
  confirmDeletePlan,
} from "./subscriptionActions";

const getInitialSubscriptionView = (search = "") => {
  const params = new URLSearchParams(search || "");
  const subscriberType = params.get("subscriberType") || undefined;
  const subscriberId = params.get("subscriberId") || "";
  const status = params.get("status") || undefined;
  const tab = params.get("tab");
  const showSubscriptions = Boolean(subscriberType || subscriberId || status || tab === "subscriptions");

  return {
    activeTab: showSubscriptions ? "subscriptions" : "plans",
    subscriptionFilters: {
      subscriberType,
      subscriberId,
      status,
    },
  };
};

export default function SubscriptionsManagementPage() {
  const { user } = useAuth();
  const actorId = user?.id || "";
  const location = useLocation();
  const initialView = getInitialSubscriptionView(location.search);
  const [activeTab, setActiveTab] = useState(initialView.activeTab);

  const [plans, setPlans] = useState([]);
  const [plansTotal, setPlansTotal] = useState(0);
  const [planLookup, setPlanLookup] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansSaving, setPlansSaving] = useState(false);
  const [planPage, setPlanPage] = useState(0);
  const [planPageSize, setPlanPageSize] = useState(10);
  const [planFilters, setPlanFilters] = useState({
    code: "",
    status: undefined,
    type: undefined,
  });
  const [planDrawerOpen, setPlanDrawerOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm] = Form.useForm();

  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionsTotal, setSubscriptionsTotal] = useState(0);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [subscriptionsSaving, setSubscriptionsSaving] = useState(false);
  const [subscriptionPage, setSubscriptionPage] = useState(0);
  const [subscriptionPageSize, setSubscriptionPageSize] = useState(10);
  const [subscriptionFilters, setSubscriptionFilters] = useState(initialView.subscriptionFilters);
  const [subscriptionDrawerOpen, setSubscriptionDrawerOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [subscriptionForm] = Form.useForm();
  const [rawDrawerOpen, setRawDrawerOpen] = useState(false);
  const [rawRecord, setRawRecord] = useState(null);
  const [rawTitle, setRawTitle] = useState("");

  const planLookupOptions = useMemo(
    () =>
      planLookup.map((plan) => ({
        value: plan.id,
        label: plan.name || plan.id,
      })),
    [planLookup],
  );

  const loadPlanLookup = useCallback(async () => {
    try {
      const response = await identitySubscriptionService.plans.list({
        page: 0,
        size: 500,
      });
      setPlanLookup(pageFromResponse(response).content);
    } catch (err) {
      message.error(err?.message || "Failed to load subscription plans");
    }
  }, []);

  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const response = await identitySubscriptionService.plans.list({
        page: planPage,
        size: planPageSize,
        code: planFilters.code || undefined,
        status: planFilters.status || undefined,
        type: planFilters.type || undefined,
      });
      const page = pageFromResponse(response);
      setPlans(page.content);
      setPlansTotal(page.total);
    } catch (err) {
      message.error(err?.message || "Failed to load subscription plans");
      setPlans([]);
      setPlansTotal(0);
    } finally {
      setPlansLoading(false);
    }
  }, [planFilters.code, planFilters.status, planFilters.type, planPage, planPageSize]);

  const loadSubscriptions = useCallback(async () => {
    const hasSubscriberType = Boolean(subscriptionFilters.subscriberType);
    const hasSubscriberId = Boolean(subscriptionFilters.subscriberId);

    if (hasSubscriberType !== hasSubscriberId) {
      message.warning("Select both subscriber type and subscriber ID to filter subscriptions");
      setSubscriptions([]);
      setSubscriptionsTotal(0);
      return;
    }

    setSubscriptionsLoading(true);
    try {
      const response = await identitySubscriptionService.subscriptions.list({
        page: subscriptionPage,
        size: subscriptionPageSize,
        subscriberType: hasSubscriberType ? subscriptionFilters.subscriberType : undefined,
        subscriberId: hasSubscriberId ? subscriptionFilters.subscriberId : undefined,
        status: subscriptionFilters.status || undefined,
      });
      const page = pageFromResponse(response);
      setSubscriptions(page.content);
      setSubscriptionsTotal(page.total);
    } catch (err) {
      message.error(err?.message || "Failed to load subscriptions");
      setSubscriptions([]);
      setSubscriptionsTotal(0);
    } finally {
      setSubscriptionsLoading(false);
    }
  }, [
    subscriptionFilters.subscriberId,
    subscriptionFilters.subscriberType,
    subscriptionFilters.status,
    subscriptionPage,
    subscriptionPageSize,
  ]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  useEffect(() => {
    const nextView = getInitialSubscriptionView(location.search);
    setActiveTab((prev) => (prev === nextView.activeTab ? prev : nextView.activeTab));
    setSubscriptionFilters((prev) => {
      const nextFilters = {
        ...prev,
        ...nextView.subscriptionFilters,
      };
      const hasChanged =
        prev.subscriberType !== nextFilters.subscriberType ||
        prev.subscriberId !== nextFilters.subscriberId ||
        prev.status !== nextFilters.status;

      if (hasChanged && nextView.activeTab === "subscriptions") {
        setSubscriptionPage(0);
      }

      return hasChanged ? nextFilters : prev;
    });
  }, [location.search]);

  const openPlanDrawer = useCallback((plan = null) => {
    setRawDrawerOpen(false);
    setRawRecord(null);
    setRawTitle("");
    setSubscriptionDrawerOpen(false);
    setEditingPlan(plan);
    planForm.setFieldsValue(getPlanFormValues(plan));
    setPlanDrawerOpen(true);
  }, [planForm]);

  const openSubscriptionDrawer = useCallback((subscription = null) => {
    setRawDrawerOpen(false);
    setRawRecord(null);
    setRawTitle("");
    setPlanDrawerOpen(false);
    setEditingSubscription(subscription);
    (async () => {
      try {
        if (!planLookup.length) {
          await loadPlanLookup();
        }
      } finally {
        subscriptionForm.setFieldsValue(getSubscriptionFormValues(subscription));
        setSubscriptionDrawerOpen(true);
      }
    })();
  }, [loadPlanLookup, planLookup.length, subscriptionForm]);

  const openRawDrawer = useCallback((record = null, type = "raw") => {
    if (!record) return;
    setPlanDrawerOpen(false);
    setEditingPlan(null);
    planForm.resetFields();
    setSubscriptionDrawerOpen(false);
    setEditingSubscription(null);
    subscriptionForm.resetFields();
    setRawRecord(record);
    setRawTitle(type === "plan" ? "Plan details" : "Subscription details");
    setRawDrawerOpen(true);
  }, [planForm, subscriptionForm]);

  const closePlanDrawer = () => {
    setPlanDrawerOpen(false);
    setEditingPlan(null);
    planForm.resetFields();
  };

  const closeSubscriptionDrawer = () => {
    setSubscriptionDrawerOpen(false);
    setEditingSubscription(null);
    subscriptionForm.resetFields();
  };

  const closeRawDrawer = () => {
    setRawDrawerOpen(false);
    setRawRecord(null);
    setRawTitle("");
  };

  const savePlan = async () => {
    try {
      const values = await planForm.validateFields();
      const payload = {
        name: values.name,
        code: values.code?.toUpperCase(),
        description: values.description || null,
        subscriptionType: values.subscriptionType,
        status: values.status || null,
        startDate: toIsoValue(values.startDate),
        expiryDate: toIsoValue(values.expiryDate),
        monthlyAmount:
          values.monthlyAmount === null || values.monthlyAmount === undefined
            ? null
            : values.monthlyAmount,
        annualAmount:
          values.annualAmount === null || values.annualAmount === undefined
            ? null
            : values.annualAmount,
      };

      setPlansSaving(true);
      if (editingPlan?.id) {
        await identitySubscriptionService.plans.update(editingPlan.id, payload);
        message.success("Subscription plan updated");
      } else {
        await identitySubscriptionService.plans.create(payload);
        message.success("Subscription plan created");
      }
      closePlanDrawer();
      await Promise.all([loadPlans(), loadPlanLookup()]);
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.message || "Failed to save subscription plan");
    } finally {
      setPlansSaving(false);
    }
  };

  const saveSubscription = async () => {
    try {
      const values = await subscriptionForm.validateFields();
      const selectedPlan = planLookup.find((plan) => plan.id === values.subscriptionPlanId);
      const freeSubscription = selectedPlan
        ? isFreePlanRecord(selectedPlan)
        : isFreePlanRecord(editingSubscription);
      const payload = {
        subscriberType: values.subscriberType,
        subscriberId: values.subscriberId,
        subscriptionPlanId: values.subscriptionPlanId || null,
        subscriptionPlanCode: selectedPlan?.code || editingSubscription?.subscriptionPlanCode || null,
        startDate: toIsoValue(values.startDate),
        endDate: toIsoValue(values.endDate),
        subscriptionPaymentType: freeSubscription ? null : values.subscriptionPaymentType || null,
        paymentStatus: freeSubscription ? null : values.paymentStatus || null,
        currency: freeSubscription ? null : values.currency || null,
        status: values.status || null,
      };

      setSubscriptionsSaving(true);
      if (editingSubscription?.id) {
        await identitySubscriptionService.subscriptions.update(editingSubscription.id, payload, actorId);
        message.success("Subscription updated");
      } else {
        await identitySubscriptionService.subscriptions.create(payload, actorId);
        message.success("Subscription created");
      }
      closeSubscriptionDrawer();
      await loadSubscriptions();
      await loadPlanLookup();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.message || "Failed to save subscription");
    } finally {
      setSubscriptionsSaving(false);
    }
  };

  const onPlanFiltersChange = useCallback((patch) => {
    setPlanFilters((prev) => ({ ...prev, ...patch }));
    if (Object.prototype.hasOwnProperty.call(patch, "code") || Object.prototype.hasOwnProperty.call(patch, "status") || Object.prototype.hasOwnProperty.call(patch, "type")) {
      setPlanPage(0);
    }
  }, []);

  const onSubscriptionFiltersChange = useCallback((patch) => {
    setSubscriptionFilters((prev) => ({ ...prev, ...patch }));
    if (
      Object.prototype.hasOwnProperty.call(patch, "subscriberType") ||
      Object.prototype.hasOwnProperty.call(patch, "subscriberId") ||
      Object.prototype.hasOwnProperty.call(patch, "status")
    ) {
      setSubscriptionPage(0);
    }
  }, []);

  const onPlanPageChange = useCallback((page, size) => {
    setPlanPage(page - 1);
    setPlanPageSize(size);
  }, []);

  const onSubscriptionPageChange = useCallback((page, size) => {
    setSubscriptionPage(page - 1);
    setSubscriptionPageSize(size);
  }, []);

  const onPlanRefresh = useCallback(async () => {
    await loadPlans();
  }, [loadPlans]);

  const onSubscriptionRefresh = useCallback(async () => {
    await loadSubscriptions();
  }, [loadSubscriptions]);

  const deletePlan = useCallback(
    (plan) =>
      confirmDeletePlan({
        plan,
        actorId,
        onDone: async () => {
          await Promise.all([loadPlans(), loadPlanLookup()]);
        },
      }),
    [actorId, loadPlanLookup, loadPlans],
  );

  const cancelSubscription = useCallback(
    (subscription) =>
      confirmCancelSubscription({
        subscription,
        actorId,
        onDone: async () => {
          await loadSubscriptions();
        },
      }),
    [actorId, loadSubscriptions],
  );

  return (
    <>
      <SubscriptionManagementTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        plans={plans}
        plansTotal={plansTotal}
        plansLoading={plansLoading}
        planPage={planPage}
        planPageSize={planPageSize}
        planFilters={planFilters}
        onPlanFiltersChange={onPlanFiltersChange}
        onPlanPageChange={onPlanPageChange}
        onPlanRefresh={onPlanRefresh}
        onOpenPlanDrawer={openPlanDrawer}
        onOpenPlanRawDrawer={(record) => openRawDrawer(record, "plan")}
        onDeletePlan={deletePlan}
        subscriptions={subscriptions}
        subscriptionsTotal={subscriptionsTotal}
        subscriptionsLoading={subscriptionsLoading}
        subscriptionPage={subscriptionPage}
        subscriptionPageSize={subscriptionPageSize}
        subscriptionFilters={subscriptionFilters}
        onSubscriptionFiltersChange={onSubscriptionFiltersChange}
        onSubscriptionPageChange={onSubscriptionPageChange}
        onSubscriptionRefresh={onSubscriptionRefresh}
        onOpenSubscriptionDrawer={openSubscriptionDrawer}
        onOpenSubscriptionRawDrawer={(record) => openRawDrawer(record, "subscription")}
        onCancelSubscription={cancelSubscription}
      />

      <SubscriptionPlanDrawer
        open={planDrawerOpen}
        editingPlan={editingPlan}
        form={planForm}
        saving={plansSaving}
        onClose={closePlanDrawer}
        onSave={savePlan}
      />

      <SubscriptionDrawer
        open={subscriptionDrawerOpen}
        editingSubscription={editingSubscription}
        form={subscriptionForm}
        saving={subscriptionsSaving}
        planLookup={planLookup}
        planLookupOptions={planLookupOptions}
        onClose={closeSubscriptionDrawer}
        onSave={saveSubscription}
      />

      <SubscriptionRawDrawer
        open={rawDrawerOpen}
        record={rawRecord}
        title={rawTitle}
        onClose={closeRawDrawer}
      />
    </>
  );
}
